import { Dispatch } from 'redux';

import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import CustomAppTypes from '../config/customAppTypes';
import { AppState } from '../reducers';
import { NafFormType } from '../reducers/naf';
import { fetchCards } from './cards';
import { fetchStatus } from './checkStatus';
import { fetchConsent } from './consent';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';
import { fetchParams } from './urlParams';
import { noCardsAndNoOngoing } from './user';

type Action<T> = import('./types').Action<T>;
// type FetchNafCacheResponse = import('../generated-types').NafResponse;
// type FetchNafMemberResponse = import('../generated-types').NafResponse;

export interface FetchNafCacheStartedAction extends Action<typeof actionTypes.FETCH_NAF_CACHE_STARTED> {
    type: typeof actionTypes.FETCH_NAF_CACHE_STARTED;
}

export interface FetchNafCacheFulfilledAction extends Action<typeof actionTypes.FETCH_NAF_CACHE_FULFILLED> {
    type: typeof actionTypes.FETCH_NAF_CACHE_FULFILLED;
    payload: any;
}

export interface FetchNafCacheFailedAction extends Action<typeof actionTypes.FETCH_NAF_CACHE_FAILED> {
    type: typeof actionTypes.FETCH_NAF_CACHE_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FetchNafMemberStartedAction extends Action<typeof actionTypes.FETCH_NAF_MEMBER_STARTED> {
    type: typeof actionTypes.FETCH_NAF_MEMBER_STARTED;
}

export interface FetchNafMemberFulfilledAction extends Action<typeof actionTypes.FETCH_NAF_MEMBER_FULFILLED> {
    type: typeof actionTypes.FETCH_NAF_MEMBER_FULFILLED;
    payload: any;
}

export interface FetchNafMemberFailedAction extends Action<typeof actionTypes.FETCH_NAF_MEMBER_FAILED> {
    type: typeof actionTypes.FETCH_NAF_MEMBER_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface FieldUpdateNafOptionAction extends Action<typeof actionTypes.FIELD_UPDATE_NAF_OPTION> {
    type: typeof actionTypes.FIELD_UPDATE_NAF_OPTION;
    payload: {
        name: string;
        value: string;
    };
}

export type NafActions =
    | FetchNafCacheStartedAction
    | FetchNafCacheFulfilledAction
    | FetchNafCacheFailedAction
    | FetchNafMemberStartedAction
    | FetchNafMemberFulfilledAction
    | FetchNafMemberFailedAction
    | FieldUpdateNafOptionAction;

export const fetchNafCacheStarted = (): FetchNafCacheStartedAction => ({
    type: actionTypes.FETCH_NAF_CACHE_STARTED,
});
export const fetchNafCacheFulfilled = (json: any): FetchNafCacheFulfilledAction => ({
    type: actionTypes.FETCH_NAF_CACHE_FULFILLED,
    payload: json,
});
export const fetchNafCacheFailed = (error: RequestExceptionWithResponse): FetchNafCacheFailedAction => ({
    type: actionTypes.FETCH_NAF_CACHE_FAILED,
    payload: error,
});

export const fetchNafMemberStarted = (): FetchNafMemberStartedAction => ({
    type: actionTypes.FETCH_NAF_MEMBER_STARTED,
});
export const fetchNafMemberFulfilled = (json: any): FetchNafMemberFulfilledAction => ({
    type: actionTypes.FETCH_NAF_MEMBER_FULFILLED,
    payload: json,
});
export const fetchNafMemberFailed = (error: RequestExceptionWithResponse): FetchNafMemberFailedAction => ({
    type: actionTypes.FETCH_NAF_MEMBER_FAILED,
    payload: error,
});

/* NAF: GET member data cache from back-end. */
export const fetchNafCache = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { loader, context } = getState();
    const { hasFetchedNafCache, isFetchingNafCache } = loader;

    if (CustomAppTypes.isNaf(context) && !hasFetchedNafCache && !isFetchingNafCache) {
        try {
            dispatch(fetchNafCacheStarted());
            const response: any = await get(
                `${actionTypes.RESOURCE_PATH}/naf`,
                getRequestHeaders(context.config.bankregnr),
            );
            dispatch(fetchNafCacheFulfilled(response));
            if (response.erMedlem) {
                const cards = await fetchCards(dispatch, getState);

                /* Fetch Params */
                await fetchParams(dispatch, getState);

                /* Fetch Status */
                const status = await fetchStatus(dispatch, getState);
                if (
                    noCardsAndNoOngoing(
                        context,
                        status,
                        !!(cards?.eksisterendeKort && cards?.eksisterendeKort?.length > 0),
                    )
                ) {
                    window.location.href = `?type=gold&endre=0`;
                }
            }
        } catch (error) {
            dispatch(fetchNafCacheFailed(error));
        }
    }
};

/* NAF: POST member data to back-end. */
export const fetchNafMember =
    (data: NafFormType) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { context, lbs } = getState();
        try {
            dispatch(fetchNafMemberStarted());
            const response: any = await post(
                `${actionTypes.RESOURCE_PATH}/naf`,
                data,
                getRequestHeaders(context.config.bankregnr),
            );

            dispatch(fetchNafMemberFulfilled(response));
            if (response.erMedlem) {
                const cards = await fetchCards(dispatch, getState);

                /* Fetch Params */
                await fetchParams(dispatch, getState);

                /* Fetch Status */
                const status = await fetchStatus(dispatch, getState);
                if (
                    noCardsAndNoOngoing(
                        context,
                        status,
                        !!(cards?.eksisterendeKort && cards?.eksisterendeKort?.length > 0),
                    )
                ) {
                    const lbsSuffix = lbs.lbsBankregnr !== null ? `&lbsbnr=${lbs.lbsBankregnr}` : '';
                    window.location.href = `?type=gold&endre=0${lbsSuffix}`;
                }
                /* Fetch Consent */
                await fetchConsent(dispatch, getState);
            }
        } catch (error) {
            dispatch(fetchNafMemberFailed(error));
        }
    };
